import { insideMarkdownLink } from './ref-guards';
import type { CardRef } from '../client';

/**
 * JIRA-style card references. Messages store only the bare token
 * (#AFB-1); the UI expands it at render time with the card's title so
 * readers know which card is meant.
 */

export const CARD_REF_PATTERN = /#([A-Za-z][A-Za-z0-9]{0,9})-(\d+)/g;

export const REF_TITLE_EXCERPT_LENGTH = 25;

/** A CardRef whose fields are guaranteed present (API types are optional) */
export interface UsableCardRef {
  reference: string;
  cardId: number;
  projectId: number;
  title: string;
}

export function usableRefs(refs: CardRef[] | undefined): UsableCardRef[] {
  return (refs || [])
    .filter(
      (ref): ref is CardRef & UsableCardRef =>
        !!ref.reference && !!ref.cardId && !!ref.projectId,
    )
    .map((ref) => ({
      reference: ref.reference,
      cardId: ref.cardId,
      projectId: ref.projectId,
      title: ref.title || '',
    }));
}

export function refTitleExcerpt(title: string): string {
  const collapsed = (title || '').replace(/\s+/g, ' ').trim();
  return collapsed.length > REF_TITLE_EXCERPT_LENGTH
    ? `${collapsed.slice(0, REF_TITLE_EXCERPT_LENGTH)}…`
    : collapsed;
}

export function cardUrl(teamId: string | number, ref: UsableCardRef): string {
  return `/console/team/${teamId}/projects/${ref.projectId}/cards/${ref.cardId}`;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const OPENING_WRAPPERS: Record<string, string> = {
  '(': ')',
  '"': '"',
  "'": "'",
  '“': '”',
};

/**
 * Authors (agents especially) tend to write the card's title right after
 * the token ("#LEH-3 Define Payload…", "#LEH-2 (\"Draft…\")"), but the
 * expanded link label already carries the title — leaving the written
 * copy in place renders the name twice. Detect an adjacent exact
 * duplicate (case-insensitive, whitespace-collapsed, optionally
 * separated by a dash/colon/comma and wrapped in quotes/parens) so the
 * expansion can consume it. Anything reworded, partial, or non-adjacent
 * is left untouched.
 */
function matchDuplicateTitle(
  text: string,
  tokenStart: number,
  tokenEnd: number,
  title: string,
): { end: number; dropLeadingBracket: boolean } | null {
  const collapsed = (title || '').replace(/\s+/g, ' ').trim();
  if (!collapsed) return null;
  let pos = tokenEnd;
  let dropLeadingBracket = false;
  if (tokenStart > 0 && text[tokenStart - 1] === '[' && text[pos] === ']') {
    pos += 1;
    dropLeadingBracket = true;
  }
  const separator = /\s*[—–\-:,]?\s*/y;
  separator.lastIndex = pos;
  const separatorMatch = separator.exec(text);
  const afterSeparator = separator.lastIndex;
  if (!separatorMatch || (afterSeparator === pos && !dropLeadingBracket)) {
    return null;
  }
  pos = afterSeparator;
  const closers: string[] = [];
  while (OPENING_WRAPPERS[text[pos]]) {
    closers.unshift(OPENING_WRAPPERS[text[pos]]);
    pos += 1;
  }
  const titlePattern = new RegExp(
    escapeRegExp(collapsed).replace(/ /g, '\\s+'),
    'iy',
  );
  titlePattern.lastIndex = pos;
  if (!titlePattern.exec(text)) return null;
  pos = titlePattern.lastIndex;
  for (const closer of closers) {
    if (text[pos] !== closer) return null;
    pos += 1;
  }
  const next = text[pos];
  if (next !== undefined && /[\p{L}\p{N}]/u.test(next)) return null;
  return { end: pos, dropLeadingBracket };
}

/** A resolved #KEY-N occurrence: [start, end) covers the token plus any
 *  consumed adjacent duplicate title (and its wrapping bracket). */
export interface CardRefSpan {
  start: number;
  end: number;
  ref: UsableCardRef;
}

/**
 * Locate known #KEY-N tokens, extending each span over an adjacent
 * exact duplicate of the card title (see matchDuplicateTitle). Unknown
 * and (when guarding) already-linked tokens are skipped.
 */
export function findCardRefSpans(
  text: string,
  refs: CardRef[] | undefined,
  guardLinks: boolean,
): CardRefSpan[] {
  const usable = usableRefs(refs);
  if (!text || usable.length === 0) return [];
  const byReference = new Map(
    usable.map((ref) => [ref.reference.toUpperCase(), ref]),
  );
  const pattern = new RegExp(CARD_REF_PATTERN.source, 'g');
  const spans: CardRefSpan[] = [];
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    const token = match[0];
    const offset = match.index!;
    if (offset < last) continue;
    const ref = byReference.get(token.toUpperCase());
    // A model may already have linked the reference itself
    // ("[#BFF-2](…)" or "[see #BFF-2 here](…)"); expanding the token
    // inside that link would nest anchors and render doubled links.
    if (!ref || (guardLinks && insideMarkdownLink(text, offset))) continue;
    let start = offset;
    let end = offset + token.length;
    const duplicate = matchDuplicateTitle(text, offset, end, ref.title);
    if (duplicate) {
      end = duplicate.end;
      if (duplicate.dropLeadingBracket) start = offset - 1;
    }
    spans.push({ start, end, ref });
    last = end;
  }
  return spans;
}

/** Replace each card-ref span via `render`, copying everything else. */
function expandCardRefs(
  text: string,
  refs: CardRef[] | undefined,
  render: (ref: UsableCardRef) => string,
  guardLinks: boolean,
): string {
  const spans = findCardRefSpans(text, refs, guardLinks);
  if (spans.length === 0) return text;
  let out = '';
  let last = 0;
  for (const span of spans) {
    out += text.slice(last, span.start);
    out += render(span.ref);
    last = span.end;
  }
  out += text.slice(last);
  return out;
}

/**
 * Replace #KEY-N tokens in plain text with markdown links whose label
 * includes the card-title excerpt — run BEFORE markdown rendering so
 * sanitization applies normally. Unknown references pass through as-is.
 */
export function expandCardRefsToMarkdown(
  text: string,
  refs: CardRef[] | undefined,
  teamId: string | number,
): string {
  return expandCardRefs(
    text,
    refs,
    (ref) =>
      `[${ref.reference} (${refTitleExcerpt(ref.title)})](${cardUrl(teamId, ref)})`,
    true,
  );
}


/**
 * The #token under the caret, if the caret is inside one — mirrors the
 * @mention detection in ChatComposer. References contain no spaces.
 */
export function cardRefTokenAt(
  text: string,
  caret: number,
): { start: number; query: string } | null {
  const hashIndex = text.lastIndexOf('#', caret - 1);
  if (hashIndex === -1) return null;
  if (hashIndex > 0 && !/\s/.test(text[hashIndex - 1])) return null;
  const query = text.slice(hashIndex + 1, caret);
  if (/\s/.test(query)) return null;
  return { start: hashIndex, query };
}

/** Rank card refs for the autocomplete: match key, reference, or title. */
export function filterCardRefs(
  refs: CardRef[] | undefined,
  query: string,
): UsableCardRef[] {
  const usable = usableRefs(refs);
  const q = query.trim().toLowerCase();
  if (!q) return usable.slice(0, 8);
  return usable
    .filter(
      (ref) =>
        ref.reference.toLowerCase().includes(q) ||
        ref.reference.toLowerCase().replace(/^#/, '').startsWith(q) ||
        ref.title.toLowerCase().includes(q),
    )
    .slice(0, 8);
}
